/**
 * FilePreviewModal — IDI-445. One modal for opening a file everywhere
 * (quote Files tab, sent quote panel, job Files tab, work order Attachments).
 *
 *   • Images render as <img>, PDFs as <iframe>, from a signed link (D3).
 *   • Other types show their details and a Download button; nothing is
 *     fetched until Download is pressed (D1).
 *   • Download asks the server for a link that saves under the filename (D4).
 *   • Library refs read their row from Settings documents (`data_url`);
 *     legacy rows use their own URL (D5).
 *   • Delete / Open source are the caller's handlers, so each screen keeps
 *     its own confirm (D2). Omit them and the buttons don't show.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { api, settingsAPI } from '../../services/api';
import { entryDisplay, formatBytes } from '../../utils/attachmentEntry';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

function previewKind(mimeType, name) {
  const mime = String(mimeType || '').toLowerCase();
  const n = String(name || '');
  if (mime.startsWith('image/') || (!mime && IMAGE_EXT.test(n))) return 'image';
  if (mime === 'application/pdf' || (!mime && /\.pdf$/i.test(n))) return 'pdf';
  if (IMAGE_EXT.test(n)) return 'image';
  if (/\.pdf$/i.test(n)) return 'pdf';
  return 'other';
}

async function resolveLibraryUrl(id) {
  const rows = await settingsAPI.getList('documents');
  const list = Array.isArray(rows) ? rows : [];
  const row = list.find((r) => r && r.id === id);
  const url = row && (row.data_url || row.url);
  if (!url) throw new Error('library document not found');
  return url;
}

/** Resolve the URL for an entry. `download` only matters for own refs. */
export async function resolveFileUrl(entry, { download = false } = {}) {
  const d = entryDisplay(entry);
  if (d.shape === 'attachment') {
    if (d.source === 'library') return resolveLibraryUrl(entry.attachmentId);
    const res = await api.getAttachmentDownloadUrlById({ attachmentId: entry.attachmentId, download });
    const url = res && (res.download_url || res.url);
    if (!url) throw new Error('no download url');
    return url;
  }
  if (d.shape === 'legacy' && d.openHref) return d.openHref;
  throw new Error('file cannot be opened');
}

function triggerDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || '';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function FilePreviewModal({ entry, onClose, onDelete, onOpenSource }) {
  const open = !!entry;
  const d = entryDisplay(entry);
  const kind = previewKind(d.mimeType, d.name);
  const inline = kind === 'image' || kind === 'pdf';

  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setUrl(null);
    setError(false);
    if (!open || !inline) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    resolveFileUrl(entry)
      .then((u) => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, d.key, inline]);

  if (!open) return null;

  const handleDownload = async () => {
    setBusy(true);
    try {
      const u = await resolveFileUrl(entry, { download: true });
      triggerDownload(u, d.name);
    } catch (e) {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try { await onDelete(); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} size="xl">
      <DialogHeader>
        <span className="break-all">{d.name}</span>
      </DialogHeader>
      <DialogBody>
        <div data-testid="file-preview-body">
          {error ? (
            <p role="alert" className="text-sm text-red-700">Couldn't open this file. Close and try again.</p>
          ) : loading ? (
            <p className="text-sm text-gray-500">Loading preview…</p>
          ) : inline && url ? (
            kind === 'image' ? (
              <img src={url} alt={d.name} className="max-h-[70vh] max-w-full mx-auto" />
            ) : (
              <iframe src={url} title={d.name} className="w-full h-[70vh] border-0" />
            )
          ) : !inline ? (
            <div className="space-y-2 text-sm">
              <p>No preview for this file type. Download it to open it.</p>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-gray-600">
                <dt>Type</dt><dd>{d.mimeType || 'Unknown'}</dd>
                <dt>Size</dt><dd>{d.bytes ? formatBytes(d.bytes) : 'Unknown'}</dd>
              </dl>
            </div>
          ) : null}
        </div>
      </DialogBody>
      <DialogFooter>
        {onDelete && (
          <Button type="button" variant="danger" className="mr-auto" onClick={handleDelete} disabled={busy}>
            Delete
          </Button>
        )}
        {onOpenSource && (
          <Button type="button" variant="secondary" onClick={onOpenSource}>
            Open source
          </Button>
        )}
        <Button type="button" variant="secondary" onClick={handleDownload} disabled={busy}>
          Download
        </Button>
        <Button type="button" variant="primary" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
